"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  Bell,
  Bug,
  CalendarDays,
  CalendarOff,
  ChevronsUpDown,
  ExternalLink,
  Home,
  ListChecks,
  LogOut,
  Monitor,
  ScanLine,
  Moon,
  PhoneCall,
  ScrollText,
  Settings,
  Sun,
  Ticket,
  UserX,
  Users,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useAuth, signOut } from "../Auth/AuthContext";
import { useTheme } from "../../utils/ThemeContext";
import { useConfig } from "../Config/ConfigContext";
import AndesStatus from "../Agenda/AndesStatus";

// Sidebar navigation. Add entries here as domain modules land; `roles`
// (optional) hides an entry unless the user holds one of the client roles —
// cosmetic only, the backend RolesGuard is the enforcement.
// Sidebar navigation, grouped into labeled sections. The first section has no
// label (the home link sits at the top). Most entries are admin-only; the
// "Recepción y sala" entries are also granted to the finer kiosk roles
// (screen → Pantalla, operator → the three reception screens, totem → Check-in).
// Keep the backend @Roles guards in sync.
const NAV_SECTIONS = [
  {
    items: [{ title: "Inicio", href: "/", icon: Home, roles: ["admin"] }],
  },
  {
    label: "Agenda y turnos",
    items: [
      {
        title: "Gestor de agendas",
        href: "/agenda",
        icon: CalendarDays,
        roles: ["admin"],
      },
      {
        title: "Listado de turnos",
        href: "/turns",
        icon: ListChecks,
        roles: ["admin"],
      },
      { title: "Pacientes", href: "/patients", icon: Users, roles: ["admin"] },
      {
        title: "Recordatorios",
        href: "/reminders",
        icon: Bell,
        roles: ["admin"],
      },
    ],
  },
  {
    label: "Recepción y sala",
    items: [
      {
        title: "Check-in por QR",
        href: "/checkin",
        icon: ScanLine,
        roles: ["admin", "operator", "totem"],
      },
      {
        title: "Sala de espera",
        href: "/attention",
        icon: PhoneCall,
        roles: ["admin", "operator"],
      },
      {
        title: "Pantalla de sala",
        href: "/display",
        icon: Monitor,
        roles: ["admin", "screen", "operator"],
      },
    ],
  },
  {
    label: "Administración",
    items: [
      {
        title: "Mi Pase",
        href: "/mi-pase",
        icon: Ticket,
        roles: ["admin"],
      },
      {
        title: "Bloqueos de autogestión",
        href: "/booking-blocks",
        icon: UserX,
        roles: ["admin"],
      },
      {
        title: "Bloqueos de prestación",
        href: "/service-blocks",
        icon: CalendarOff,
        roles: ["admin"],
      },
      {
        title: "Registro de auditoría",
        href: "/audit",
        icon: ScrollText,
        roles: ["admin"],
      },
      {
        title: "Errores",
        href: "/errors",
        icon: Bug,
        roles: ["admin"],
      },
      {
        title: "Configuración",
        href: "/settings",
        icon: Settings,
        roles: ["admin"],
      },
    ],
  },
  {
    label: "Público",
    items: [
      {
        // Redirect to the public self-service booking page (opens in a new
        // tab); it has no sidebar and needs no login. Here so staff can
        // preview it.
        title: "Reserva pública",
        href: "/book",
        icon: ExternalLink,
        roles: ["admin"],
        external: true,
        // Hidden when an admin has turned off the public booking channel
        // (GET /api/config → publicBooking.enabled).
        requiresPublicBooking: true,
      },
    ],
  },
];

function initialsOf(user) {
  const source = user?.name ?? user?.email ?? "?";
  return source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function SidebarLogo() {
  const config = useConfig();
  const branding = config?.branding ?? {};
  const title = branding.title || "Neuquen";
  const subtitle = branding.subtitle || "";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton size="lg" asChild>
          <Link href="/">
            <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg font-bold">
              {title.charAt(0).toUpperCase() || "N"}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{title}</span>
              {subtitle && (
                <span className="text-muted-foreground truncate text-xs">
                  {subtitle}
                </span>
              )}
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function SidebarUser() {
  const { user, hasRole } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const displayName = user?.name ?? user?.email ?? "Cuenta";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg">
                  {initialsOf(user)}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{displayName}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {user?.email}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side="top"
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="font-normal">
              <div className="grid text-sm leading-tight">
                <span className="truncate font-semibold">{displayName}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {user?.email}
                  {hasRole("admin") ? " · admin" : ""}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleTheme}>
              {theme === "dark" ? <Sun /> : <Moon />}
              {theme === "dark" ? "Modo claro" : "Modo oscuro"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()}>
              <LogOut />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export default function AppLayout({ children }) {
  const router = useRouter();
  const { hasRole } = useAuth();
  const config = useConfig();
  // Public booking is on unless an admin has explicitly turned it off.
  const publicBookingEnabled = config?.publicBooking?.enabled !== false;
  // Filter each section's items by role and feature flags, then drop sections
  // left empty.
  const sections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.roles || item.roles.some((role) => hasRole(role))) &&
        (!item.requiresPublicBooking || publicBookingEnabled),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <SidebarLogo />
        </SidebarHeader>
        <SidebarContent>
          {sections.map((section, i) => (
            <SidebarGroup key={section.label ?? `section-${i}`}>
              {section.label && (
                <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          !item.external && router.pathname === item.href
                        }
                        tooltip={item.title}
                      >
                        <Link
                          href={item.href}
                          {...(item.external
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : {})}
                        >
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter>
          <AndesStatus />
          <SidebarUser />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
        </header>
        <main className="flex-1 p-6">{children}</main>
      </SidebarInset>
    </SidebarProvider>
  );
}
